import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, Star, ChevronLeft, ChevronRight, Loader2, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { serviceMarketplaceAPI } from '../services/api';
import CategoryGrid from '../components/CategoryGrid';
import ProviderCard from '../components/ProviderCard';

const ServiceMarketplacePage = () => {
  const navigate = useNavigate();
  const { user, activeMode } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [postcode, setPostcode] = useState(searchParams.get('postcode') || '');
  const [postcodeInput, setPostcodeInput] = useState(searchParams.get('postcode') || '');
  const [minRating, setMinRating] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('rating');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Load categories
  useEffect(() => {
    serviceMarketplaceAPI.getCategories().then(setCategories).catch(console.error);
  }, []);

  // Load providers when filters change
  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { page, sort_by: sortBy };
      if (selectedCategory) filters.category = selectedCategory;
      if (selectedSubcategory) filters.subcategory = selectedSubcategory;
      if (postcode) filters.postcode = postcode;
      if (minRating) filters.min_rating = minRating;
      if (maxPrice) filters.max_price = maxPrice;
      if (verifiedOnly) filters.verified_only = true;

      const data = await serviceMarketplaceAPI.browseProviders(filters);
      setProviders(data.providers || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      console.error('Failed to load providers:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedSubcategory, postcode, minRating, maxPrice, verifiedOnly, sortBy, page]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleSearch = () => {
    setPostcode(postcodeInput.trim());
    setPage(1);
  };

  const handleCategorySelect = (catId) => {
    setSelectedCategory(catId || '');
    setSelectedSubcategory('');
    setPage(1);
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setPostcode('');
    setPostcodeInput('');
    setMinRating('');
    setMaxPrice('');
    setVerifiedOnly(false);
    setSortBy('rating');
    setPage(1);
  };

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  const hasFilters = selectedCategory || postcode || minRating || maxPrice || verifiedOnly;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#1F3A68] to-[#2d5499] text-white">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16">
          {/* Back button */}
          <button
            onClick={() => {
              if (user && activeMode === 'host') {
                navigate('/host/dashboard');
              } else {
                navigate(-1);
              }
            }}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">{user && activeMode === 'host' ? 'Back to Host Dashboard' : 'Back'}</span>
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold text-center mb-3">Find Service Providers</h1>
          <p className="text-center text-white/80 mb-8 max-w-xl mx-auto">
            Browse cleaners, handymen, decorators, and more. Get quotes from local professionals.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto flex gap-2">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Enter your postcode (e.g. SW1, E1, B1)"
                value={postcodeInput}
                onChange={e => setPostcodeInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-3 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-white text-[#1F3A68] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Non-circumvention notice — surfaced at the moment a host is about
            to engage a Service Provider, so the off-platform rules are
            unmissable. See T&Cs Section 13A. */}
        <div className="mb-6 rounded-xl border-2 border-amber-400 bg-amber-50 p-4">
          <p className="text-amber-900 font-bold uppercase tracking-wide text-xs mb-1">
            ⚠ Keep this relationship on Ariv
          </p>
          <p className="text-amber-950 text-sm leading-relaxed">
            If You hire a Service Provider through Ariv, all future work with them must also go through Ariv for the next 24 months. Engaging them off-platform breaches our Terms and triggers account termination plus recovery of the fees You attempted to avoid plus a £500 administrative charge per breach. Same rule applies to Providers approaching You for off-platform work.{' '}
            <a
              href="/terms#non-circumvention"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold hover:text-amber-700"
            >
              Read the full clause (Section 13A)
            </a>
          </p>
        </div>

        {/* Categories */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Browse by Category</h2>
          <CategoryGrid
            categories={categories}
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
          />
        </div>

        {/* Subcategories */}
        {selectedCategoryData && selectedCategoryData.subcategories?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-600 mb-2">
              {selectedCategoryData.label} — Specializations
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedSubcategory('')}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  !selectedSubcategory ? 'bg-[#1F3A68] text-white border-[#1F3A68]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              {selectedCategoryData.subcategories.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => { setSelectedSubcategory(sub.id); setPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    selectedSubcategory === sub.id ? 'bg-[#1F3A68] text-white border-[#1F3A68]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {sub.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-gray-900">
              {total} Provider{total !== 1 ? 's' : ''} Found
            </h2>
            {hasFilters && (
              <button onClick={clearFilters} className="text-sm text-[#CB3B3B] hover:underline flex items-center gap-1">
                <X size={14} /> Clear filters
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm ${showFilters ? 'bg-[#1F3A68] text-white' : 'bg-white text-gray-600'}`}
            >
              <SlidersHorizontal size={14} /> Filters
            </button>
            <select
              value={sortBy}
              onChange={e => { setSortBy(e.target.value); setPage(1); }}
              className="border rounded-lg px-3 py-1.5 text-sm bg-white"
            >
              <option value="rating">Best Rated</option>
              <option value="price">Lowest Price</option>
              <option value="reviews">Most Reviews</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200 flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Min Rating</label>
              <select value={minRating} onChange={e => { setMinRating(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-1.5 text-sm">
                <option value="">Any</option>
                <option value="4">4+ Stars</option>
                <option value="3">3+ Stars</option>
                <option value="2">2+ Stars</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1">Max Price (£/hr)</label>
              <select value={maxPrice} onChange={e => { setMaxPrice(e.target.value); setPage(1); }} className="border rounded-lg px-3 py-1.5 text-sm">
                <option value="">Any</option>
                <option value="20">Under £20</option>
                <option value="30">Under £30</option>
                <option value="50">Under £50</option>
                <option value="100">Under £100</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={e => { setVerifiedOnly(e.target.checked); setPage(1); }}
                className="rounded"
              />
              <span className="text-sm text-gray-700">Verified only</span>
            </label>
          </div>
        )}

        {/* Results Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1F3A68]" size={32} />
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-20">
            <Search size={48} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-600">No providers found</h3>
            <p className="text-gray-400 mt-1">Try adjusting your filters or search in a different area</p>
            <button
              onClick={() => navigate('/services/request')}
              className="mt-4 px-6 py-2 bg-[#1F3A68] text-white rounded-lg hover:bg-[#162d52]"
            >
              Post a Service Request Instead
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {providers.map(provider => (
                <ProviderCard key={provider.providerId} provider={provider} />
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm text-gray-600 px-3">
                  Page {page} of {pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-2 rounded-lg border disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-[#1F3A68] to-[#2d5499] rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Can't find what you need?</h2>
          <p className="text-white/80 mb-6">Post a service request and let providers come to you with quotes.</p>
          <button
            onClick={() => navigate('/services/request')}
            className="px-8 py-3 bg-white text-[#1F3A68] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            Post a Service Request
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceMarketplacePage;
